import React, { useEffect, useRef, useState } from 'react';

import { DatasetDataInfo } from 'models/dataset-models';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useScadaStore } from 'store/scada-store';
import { DatasetDataInfoResponse } from 'api/model/dataset/DatasetDataInfoResponse';
import CalendarWrapper from '../../basic/calendar-wrapper/CalendarWrapper';
import DatasetLineChart from '../charts/dataset-line-chart/DatasetLineChart';
import './Scada.scss';
import { ScadaTag } from './ScadaTag';
import { ParentRect, ScadaElementsProps, ScadaProps } from './scadaTypes';
import { cancelSelections, getScadaData, getScadaImageUrl, handleContextMenu, showCords } from './scadaUtils';

const defaultElementParameters: ParentRect = {
	width: 0,
	height: 0,
	top: 0,
	left: 0,
};

const defaultDateTime = {
	date: new Date(),
	manualTime: false,
};

export default function Scada(props: ScadaProps) {
	const [parent, setParent] = useState(defaultElementParameters);
	const [dialog, setDialog] = useState(false);
	const [selectedIdentifiers, setSelectedIdentifiers] = useState<string[]>([]);
	const [dateTime, setDateTime] = useState(defaultDateTime);
	const ref = useRef<HTMLDivElement>(null);
	const scadaStore = useScadaStore();

	const scadaLabels = scadaStore.scadaMap.get(props.scadaId);
	let timeout: NodeJS.Timeout | null = null;

	useEffect(() => {
		if (!scadaLabels) {
			scadaStore.addScada({
				scadaId: props.id ?? props.scadaId,
				labels: props.elements,
			});
		} else {
			scadaStore.updateScadaLabels(props.scadaId, props.elements);
		}
	}, []);

	useEffect(() => {
		ref.current?.addEventListener('click', cancelSelections);
		ref.current?.addEventListener('mousemove', showCords);
		ref.current?.addEventListener('contextmenu', onContextMenu);

		const rect = ref.current!.getBoundingClientRect();
		setParent({
			width: rect.width,
			height: rect.height,
			top: rect.top,
			left: rect.left,
		});
		return () => {
			ref.current?.removeEventListener('click', cancelSelections);
			ref.current?.removeEventListener('mousemove', showCords);
			ref.current?.removeEventListener('contextmenu', onContextMenu);
		};
	}, [ref.current]);

	const handleScroll = () => {
		if (timeout) {
			clearTimeout(timeout);
		}
		timeout = setTimeout(() => {
			const rect = ref.current!.getBoundingClientRect();
			setParent({
				width: rect.width,
				height: rect.height,
				top: rect.top,
				left: rect.left,
			});
		}, 200);
	};

	useEffect(() => {
		window.addEventListener('scroll', handleScroll);

		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const onContextMenu = (e: MouseEvent) => {
		handleContextMenu(e, openDialog);
	};

	const openDialog = () => {
		setSelectedIdentifiers([
			...new Set(window.scada.selectedLabels.map(l => l.dataset.identifier)),
		]);
		setDialog(true);
	};

	const elemProps: ScadaElementsProps = {
		...props,
		date: dateTime.manualTime ? dateTime.date : undefined,
		returnDate: e => setDateTime({ ...dateTime, date: e }),
		openDialogCallBack: openDialog,
		parent,
	};

	return (
		<>
			<div id='tooltip-container'></div>
			<div className='flex flex-column gap-3'>
				{props.showScadaSettings && (
					<div className='flex gap-1'>
						<CalendarWrapper
							value={dateTime.date}
							onChange={e => setDateTime({ date: e, manualTime: true })}
						/>

						{dateTime.manualTime && (
							<Button
								icon='pi pi-sync'
								disabled={!dateTime.manualTime}
								onClick={() => {
									setDateTime({
										date: new Date(),
										manualTime: false,
									});
								}}
							/>
						)}
					</div>
				)}

				<div ref={ref} className='scadaViewContainer relativePos'>
					<img
						alt='SCADA schematics'
						className='scadaView'
						src={getScadaImageUrl(props.scadaBackgroundId)}
					/>

					<ScadaElements {...elemProps} />
				</div>
			</div>

			<Dialog
				header='SCADA tags chart'
				visible={dialog}
				style={{ width: 'min(100vw, 1000px)', height: '500px' }}
				modal={false}
				resizable={true}
				draggable={true}
				maximizable
				onHide={() => setDialog(false)}>
				<DatasetLineChart
					identifiers={selectedIdentifiers}
					referenceTime={
						dateTime.manualTime ? dateTime.date.toISOString() : undefined
					}
					showDateSelection={true}
					dynamicHeight={true}
					chartDialogOpen={dialog}
				/>
			</Dialog>
		</>
	);
}

function ScadaElements(props: ScadaElementsProps) {
	const [dataMap, setDataMap] = useState<Map<String, DatasetDataInfo>>(
		new Map(),
	);

	useEffect(() => {
		setDataMap(new Map());
		loadData();

		const int = setInterval(loadData, (props.refreshRate ?? 60) * 1000);

		return () => {
			console.log('Cleaning up SCADA refresh interval');
			clearInterval(int);
		};
	}, [props.date]);

    const loadData = () => {
        const chunkSize = 16
        const tmpMap = dataMap ?? new Map();
        let count = 0
        const identifiers = props.elements.map(e => e.identifier);

		while (count < identifiers.length) {
			const chunk = identifiers.slice(count, (count += chunkSize));

			getScadaData(chunk, props.date?.toISOString()).then(
				(data: Array<DatasetDataInfoResponse>) => {
					const currentDate = new Date();
					data.forEach(d => {
						tmpMap.set(d.identifier, {
							...d,
							loadedTime: props.date || currentDate,
						} as DatasetDataInfo);
					});

					if (!props.date) {
						props.returnDate(currentDate);
					}

					setDataMap(new Map(tmpMap));
				},
			);
		}
	};

    return (
			<>
				{[...props.elements].map(el => {
					const dataset = dataMap.get(el.identifier);

					return (
						<ScadaTag
							key={el.identifier}
							model={props}
							scadaId={props.scadaId}
							x={el.xCoord}
							y={el.yCoord}
							dataset={dataset}
							identifier={el.identifier}
							bgColor={el.bgColor}
							fontSize={el.fontSize}
							refTime={props.date}
							parent={props.parent}
						/>
					);
				})}
			</>
		);
}
